import { ReactNode, useId, useState } from 'react';
import { format, isValid, parse } from 'date-fns';

import type { FieldSize } from '@/components/ui/input';

import { FieldClearButton } from '@/components/FieldClearButton';
import { InputLabel } from '@/components/InputLabel';
import { PickerTrigger } from '@/components/PickerTrigger';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

import { DISPLAY_DATES_FORMAT, SEND_DATES_FORMAT } from '@/constants/dates';

function parseDatePickerValue(raw: string, pattern: string): Date | undefined {
  let parsed: Date;
  try {
    parsed = parse(raw, pattern, new Date());
  } catch {
    return undefined;
  }
  if (!isValid(parsed)) {
    return undefined;
  }
  return parsed;
}

type DatePickerProps = {
  value?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  label?: ReactNode;
  hint?: ReactNode;
  isRequired?: boolean;
  placeholder?: string;
  disabled?: boolean;
  size?: FieldSize;
  className?: string;
  id?: string;
  dateFormat?: string;
  displayDateFormat?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  showYearDropdown?: boolean;
  fromYear?: number;
  toYear?: number;
};

const DatePicker = (props: DatePickerProps) => {
  const {
    value,
    onChange,
    onClear,
    label,
    hint,
    isRequired,
    placeholder,
    size = 'md',
    disabled,
    className,
    id,
    dateFormat = SEND_DATES_FORMAT,
    displayDateFormat = DISPLAY_DATES_FORMAT,
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    showYearDropdown = true,
    fromYear,
    toYear,
  } = props;

  const generatedId = useId();
  const pickerId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(() => new Date());

  const dateValue = value ? parseDatePickerValue(value, dateFormat) : undefined;

  const displayValue = dateValue ? format(dateValue, displayDateFormat) : '';

  const onDateSelect = (date: Date | undefined) => {
    if (date) {
      const serverValue = format(date, dateFormat);
      onChange?.(serverValue);
    }
    setIsOpen(false);
  };

  const onMonthChange = (month: Date) => {
    setDisplayMonth(month);

    if (dateValue) {
      const currentDay = dateValue.getDate();
      const lastDay = new Date(
        month.getFullYear(),
        month.getMonth() + 1,
        0,
      ).getDate();
      const clampedDay = Math.min(currentDay, lastDay);
      const newDate = new Date(
        month.getFullYear(),
        month.getMonth(),
        clampedDay,
      );
      onChange?.(format(newDate, dateFormat));
    }
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setDisplayMonth(dateValue || new Date());
    }
  };

  const clearButton = onClear && value && !disabled && (
    <FieldClearButton onClear={onClear} />
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <InputLabel
          htmlFor={pickerId}
          label={label}
          hint={hint}
          isRequired={isRequired}
          isInvalid={ariaInvalid}
        />
      )}
      <Popover
        open={isOpen}
        onOpenChange={onOpenChange}
      >
        <PopoverTrigger asChild>
          <PickerTrigger
            id={pickerId}
            aria-label={ariaLabel}
            fieldSize={size}
            disabled={disabled}
            isInvalid={ariaInvalid}
            isOpen={isOpen}
            displayValue={displayValue}
            placeholder={placeholder}
            clearButton={clearButton}
          />
        </PopoverTrigger>
        <PopoverContent
          variant="dropdown"
          className="w-(--radix-popover-trigger-width) max-w-105 min-w-85 rounded-[12px] p-4"
          align="start"
        >
          <Calendar
            mode="single"
            month={displayMonth}
            onMonthChange={onMonthChange}
            selected={dateValue}
            onSelect={onDateSelect}
            autoFocus
            captionLayout={showYearDropdown ? 'dropdown' : 'label'}
            startMonth={new Date(fromYear ?? 1920, 0)}
            endMonth={new Date(toYear ?? new Date().getFullYear() + 10, 11)}
            className="w-full p-0 [--cell-size:44px]"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { DatePicker };
export type { DatePickerProps };
